import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectGridFilters,
  selectGridSortInfo,
  selectGridPagination,
  updateGridFilters,
  updateGridSort,
  updateGridPagination,
} from '@/store/slices/grid/gridStateSlice';
import { generateFilterColumns } from './helper';

// Slice stores sortInfo as array; ReactDataGrid expects a single object
const normaliseSortInfo = (val) => {
  if (!val) return null;
  if (Array.isArray(val)) return val.length ? val[0] : null;
  return val;
};

const isEmptySelectValue = (value) =>
  value === "" ||
  value === undefined ||
  (Array.isArray(value) && value.length === 0);

const normalizeFilterValue = (filter) => {
  if (!filter) return filter;
  if (filter.type === "select" && isEmptySelectValue(filter.value)) {
    return { ...filter, value: null };
  }
  return filter;
};

const mergeFilters = (defaults, saved) => {
  if (!saved) return defaults;
  return defaults.map((def) => {
    const savedFilter = saved.find((s) => s.name === def.name);
    if (!savedFilter) return def;
    return normalizeFilterValue({
      ...def,
      ...savedFilter,
      value: savedFilter.value ?? def.value,
    });
  });
};

export const useGridFilters = (gridId, columns) => {
  const dispatch = useDispatch();

  const savedFilters  = useSelector(state => gridId ? selectGridFilters(gridId)(state)  : null);
  const savedSortInfo = useSelector(state => gridId ? selectGridSortInfo(gridId)(state) : null);
  const savedPagination = useSelector(state => gridId ? selectGridPagination(gridId)(state) : null);

  const generatedDefaults = generateFilterColumns(columns);

  const [filterValue, setFilterValue] = useState(() => mergeFilters(generatedDefaults, savedFilters));
  const [sortInfo, setSortInfo] = useState(() => normaliseSortInfo(savedSortInfo));
  const [pagination, setPagination] = useState(() => savedPagination ?? { skip: 0, limit: 25 });

  useEffect(() => {
    if (savedFilters) setFilterValue(mergeFilters(generatedDefaults, savedFilters));
  }, [savedFilters, columns]);

  useEffect(() => {
    if (savedSortInfo) setSortInfo(normaliseSortInfo(savedSortInfo));
  }, [savedSortInfo]);

  useEffect(() => {
    if (savedPagination) setPagination(savedPagination);
  }, [savedPagination]);

  useEffect(() => {
    if (!savedFilters && columns?.length) {
      setFilterValue(generateFilterColumns(columns));
    }
  }, [columns?.length]);

  const defaultFilterValue = mergeFilters(generatedDefaults, savedFilters);
  const defaultSortInfo    = normaliseSortInfo(savedSortInfo);
  const defaultPagination  = savedPagination ?? { skip: 0, limit: 25 };

  const onFilterValueChange = (e) => {
    const next = Array.isArray(e) ? e.map(normalizeFilterValue) : e;
    setFilterValue(next);
    if (!gridId) return;
    if (JSON.stringify(next) !== JSON.stringify(savedFilters)) {
      dispatch(updateGridFilters({ gridId, filters: next }));
    }
  };

  const onSortInfoChange = (e) => {
    setSortInfo(e);
    if (!gridId) return;
    const toStore = Array.isArray(e) ? e : e ? [e] : [];
    if (JSON.stringify(toStore) !== JSON.stringify(savedSortInfo)) {
      dispatch(updateGridSort({ gridId, sortInfo: toStore }));
    }
  };

  const onPaginationChange = (e) => {
    const newPagination = { skip: e.skip, limit: e.limit };
    setPagination(newPagination);
    if (!gridId) return;
    if (JSON.stringify(newPagination) !== JSON.stringify(savedPagination)) {
      dispatch(updateGridPagination({ gridId, pagination: newPagination }));
    }
  };

  const onSkipChange = (skip) => {
    const newPagination = { ...pagination, skip };
    setPagination(newPagination);
    if (!gridId) return;
    dispatch(updateGridPagination({ gridId, pagination: newPagination }));
  };

  const onLimitChange = (limit) => {
    const newPagination = { ...pagination, limit, skip: 0 }; // reset skip when limit changes
    setPagination(newPagination);
    if (!gridId) return;
    dispatch(updateGridPagination({ gridId, pagination: newPagination }));
  };

  return {
    filterValue,
    sortInfo,
    pagination,
    defaultFilterValue,
    defaultSortInfo,
    defaultPagination,
    onFilterValueChange,
    onSortInfoChange,
    onPaginationChange,
    onSkipChange,
    onLimitChange,
  };
};